// `/checkout`'S BEFORE-YOU-PAY PANEL AND ITS CALM CAN-RAISE CARD (WEB-DESIGN-1, R234).
//
// Tyler, after walking the test site (2026-10-05): "The screenshotted page… looks boring… it could look more exciting",
// "Overall these screens need more dimension", and "why are we scaring them away immediately with the nebraska can raise
// your assessment piece". His decisions 3 (A) and 5 (docs/legal/WEB_PURCHASE_POLICIES.md, "Tyler's test-site walk").
//
// THE PANEL IS THE APP'S CREDIT-CONFIRM SHEET, LAID OUT AS A HERO (AppealEntryFlow.swift:424-503). Every word on it is the
// app's or an approved one: the purchase sheet's title and body (`purchase.hero.*`), the home the server resolved, the
// unit line, the sheet's five cover rows with the app's own symbols (`creditCoverRow`, AEF:460-464), and its M3 line
// (AEF:481, approved 2026-09-30 for both clients).
//
// THE LETTER PAGE IS A DRAWING, NOT A PICTURE OF A LETTER (WALK-FIX-2; Tyler's second walk, 2026-10-05, on the blurred
// sample page: "can we just do a documment with lines or something.. cartoony looking pdf"). `LetterIllustration` is
// inline SVG: a white page with a folded corner, an amber heading bar, grey lines for text, two amber bullets, a signature
// squiggle on its line and a small green seal with a check. It has NO WORDS — no <text>, nothing a reader could take as a
// claim — so it is `aria-hidden`. Vector, so it is crisp at any size; it carries its own white paper and grey outline, so
// it reads the same on the navy page and on a light one. It replaced `public/sample-letter-page.jpg` (4 KB), now deleted.
//
// WHAT IS NOT HERE, AND WHY (the report's drafts and escalations):
//   - THE HOME'S PHOTO. Before a credit is spent the server has no record and sends no coordinates (op "address" answers
//     `subject: null`), and drawing Apple's satellite view before purchase would send the property's position to Apple
//     earlier than the approved privacy sentence covers. That is Tyler's call (what vendors receive), so the panel shows
//     the address with the app's house symbol until he rules.
//   - THE FREE CHECK'S HIGHLIGHTS, UNTIL R-pending-web-free-first. The free check hands this site an address and a unit
//     as text for two boxes and nothing else (`app/handIn.ts`). Since 2.31.0 the page asks the server for them by appeal,
//     while the server's FREE_CHECK_V2_ENABLED is on, and passes them in as `band` (`routes/freeFirst.tsx`); off, there is
//     no band and this panel is as it was.
//
// SHORTER WITH A CREDIT IN HAND (R251; Tyler, 2026-10-13, on `/checkout`: "i like the top part of
// this page but the bottom gets too long.. we need to encourage people to buy a credit and try it rather than scaring them
// away"). With no credit the panel is exactly as it was: the title and sub, the letter page, the five cover rows and M3.
// With a credit to spend there is nothing left to sell, so `compact` keeps only the home, the title, the sub (already one
// approved sentence, `purchase.hero.body`) and the letter page beside them; the cover rows and M3 are not drawn. M3 moves
// to the credit pop-up, where it says when the credit is used (`Checkout.tsx`). No word is added or changed.
//
// THE CAN-RAISE NOTICE IS ONE CALM LINE IN THE BUY CARD (R251, Tyler's "for raise.. B",
// 2026-10-13; it was a calm card above the money under WEB-DESIGN-1's decision 3 = A): the app's title for G-2's card and
// "You'll be asked to confirm you understand this before we generate anything.", under the Buy button. The full warning and
// its checkbox at the Generate step are unchanged: that step renders `canRaise.card` itself (`steps/You.tsx`).

import type { ReactNode } from "react";
import type { CanRaise } from "@contract";
import { Icon, type IconName } from "./steps/parity4Parts";
import { CONFIRM_ROWS, prePayTemplate, prePayText, type PrePayId } from "../components/prePayCopy";
import { canRaiseTitle } from "./nextStep";

/** The app's symbol for each cover row (AEF:460-464): mappin.and.ellipse, checkmark.seal.fill, chart.line.uptrend.xyaxis,
 *  doc.text.fill, calendar. The shared icon set draws three; the uptrend and the calendar are drawn here. */
const ROW_ICONS: Readonly<Record<(typeof CONFIRM_ROWS)[number], IconName | "uptrend" | "calendar">> = {
  "confirm.row.comps": "pin",
  "confirm.row.verdict": "seal",
  "confirm.row.value": "uptrend",
  "confirm.row.letter": "doc",
  "confirm.row.deadline": "calendar",
};

export function CalendarIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M7 2h2v2h6V2h2v2h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2zm-2 7v10h14V9zm2 2h3v3H7z"
      />
    </svg>
  );
}

/** chart.line.uptrend.xyaxis: the shared set's "trend" points down, and the app's row points up. */
export function UptrendIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 20h18M4 16l5-5 4 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 7h5v5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The text lines of the drawn page: [y, width] in the drawing's 220 × 285 units, every line starting at x = 20. */
const LINES: readonly (readonly [number, number])[] = [
  [68, 180], [80, 172], [92, 178], [104, 120],
  [124, 176], [136, 180], [148, 164], [160, 98],
];
/** The two bulleted lines under them: [y, width], each after an amber dot. */
const BULLETS: readonly (readonly [number, number])[] = [[182, 112], [196, 128]];

/** WALK-FIX-2: a page of a letter, drawn — lines, not words (see the file's header). */
export function LetterIllustration() {
  return (
    <svg
      className="letter-illustration"
      viewBox="0 0 220 285"
      width={220}
      height={285}
      aria-hidden="true"
      focusable="false"
      data-letter-illustration
    >
      <path
        d="M11 1H189L219 31V274A10 10 0 0 1 209 284H11A10 10 0 0 1 1 274V11A10 10 0 0 1 11 1Z"
        fill="#ffffff"
        stroke="#d6d9e6"
        strokeWidth="1.5"
      />
      <path d="M189 1V21A10 10 0 0 0 199 31H219Z" fill="#e4e7f0" stroke="#d6d9e6" strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="20" y="24" width="112" height="13" rx="6.5" fill="#fca311" />
      <rect x="20" y="46" width="64" height="6" rx="3" fill="#c3c8d8" />
      {LINES.map(([y, w]) => (
        <rect key={y} x="20" y={y} width={w} height="6" rx="3" fill="#dde0ea" />
      ))}
      {BULLETS.map(([y, w]) => (
        <g key={y}>
          <circle cx="24" cy={y + 3} r="3" fill="#fca311" />
          <rect x="34" y={y} width={w} height="6" rx="3" fill="#dde0ea" />
        </g>
      ))}
      <path
        d="M24 244C30 228 38 226 36 240C35 248 42 246 48 236C52 229 56 230 55 238C54 245 62 243 68 234C71 230 76 231 78 238C80 243 90 240 100 232"
        fill="none"
        stroke="#1d2440"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="20" y="252" width="96" height="1.5" rx="0.75" fill="#c3c8d8" />
      <path
        d="M184 217L188.1 220.5L193.5 219.5L195.3 224.7L200.5 226.5L199.5 231.9L203 236L199.5 240.1L200.5 245.5L195.3 247.3L193.5 252.5L188.1 251.5L184 255L179.9 251.5L174.5 252.5L172.7 247.3L167.5 245.5L168.5 240.1L165 236L168.5 231.9L167.5 226.5L172.7 224.7L174.5 219.5L179.9 220.5Z"
        fill="#1f8a5b"
      />
      <path d="M176.5 236.5L181.5 241.5L191.5 231" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The home the credit would go to, the purchase sheet's title and body, the cover rows, M3 and a page of a letter.
 *  `compact` (an owner with a credit to spend): the home, the title, the body and the page only.
 *  `band` (R-pending-web-free-first): the free check's first look, directly under the top row, in both layouts — or
 *  nothing, when the server's switch is off or the free check gave no answer, and the panel is exactly as before. */
export function PrePayPanel({ address, unit, compact = false, band = null }: {
  address: string | null;
  unit: string | null;
  compact?: boolean;
  band?: ReactNode;
}) {
  return (
    <section
      className={compact ? "prepay-hero compact" : "prepay-hero"}
      aria-labelledby="prepay-title"
      data-prepay-hero
      data-prepay-compact={compact ? "" : undefined}
    >
      <div className="prepay-top">
        <div className="prepay-intro">
          {address !== null && (
            <div className="prepay-home" data-prepay-home>
              <span className="prepay-home-tile" aria-hidden="true">
                <Icon name="house" size={22} />
              </span>
              <div className="prepay-home-text">
                <p className="prepay-address" data-address={address}>{address}</p>
                {unit !== null && (
                  <p className="prepay-unit" data-app-copy="confirm.unit">{prePayTemplate("confirm.unit", { unit })}</p>
                )}
              </div>
            </div>
          )}
          <h1 id="prepay-title" data-app-copy="purchase.hero.title">{prePayText("purchase.hero.title")}</h1>
          <h2 className="sub" data-app-copy="purchase.hero.body">{prePayText("purchase.hero.body")}</h2>
        </div>
        <figure className="letter-preview" aria-hidden="true" data-letter-preview>
          <LetterIllustration />
        </figure>
      </div>
      {band}
      {!compact && (
        <>
          <ul className="cover-rows" data-cover-rows>
            {CONFIRM_ROWS.map((id: PrePayId & (typeof CONFIRM_ROWS)[number]) => {
              const icon = ROW_ICONS[id];
              return (
                <li key={id} data-app-copy={id}>
                  <span className="cover-icon" aria-hidden="true">
                    {icon === "calendar" ? <CalendarIcon /> : icon === "uptrend" ? <UptrendIcon /> : <Icon name={icon} size={18} />}
                  </span>
                  <span>{prePayText(id)}</span>
                </li>
              );
            })}
          </ul>
          <p className="prepay-m3" data-app-copy="confirm.m3">{prePayText("confirm.m3")}</p>
        </>
      )}
    </section>
  );
}

/**
 * THE CAN-RAISE NOTICE OF THE APP'S PURCHASE SHEET, AS ONE CALM LINE IN THE BUY CARD (R251; Tyler,
 * 2026-10-13, choosing between "A: full card above Buy, as today; B: one calm line below Buy, with 'You'll be asked to
 * confirm you understand this before we generate anything.'": "for raise.. B"). Shown for the two tiers where filing can
 * raise the owner's value, `canRaise` and `deNovoRisk`, and for nothing else, as the app's card is (BuyPacketsSheet.swift
 * `backfireCard`): no line for `cannotRaiseAtBoard` (nothing to disclose) or `unknown` ("warning about an unknown risk on
 * the paywall would fire for nearly everyone"). Its words: the app's title for the card G-2 sent (`canRaiseTitle`), then
 * the app's `canRaise.confirmLater`. The server's longer card sentence is not on this page any more; the Generate step,
 * where the owner confirms it, still shows it whole beside its checkbox (`steps/You.tsx`, `data-compliance`).
 * WHERE NO TITLE EXISTS for the card the server sent (a code with no app title, or Nebraska's card with no state name),
 * the line carries the server's own sentence in the title's place, so "this" in the confirm-later line always points at
 * something said. No word is new. Superseded: the calm CARD above the money (WEB-DESIGN-1 decision 3 = A); `calmOrder`
 * (`prePayRules.ts`) was its ordering and is no longer drawn here.
 */
export function CanRaiseLine({ canRaise, state }: { canRaise: CanRaise; state: string | null }) {
  if (canRaise.card === null || (canRaise.risk !== "canRaise" && canRaise.risk !== "deNovoRisk")) return null;
  const title = canRaiseTitle(canRaise.card.code, state);
  return (
    <div className="can-raise-line" data-can-raise={canRaise.risk} data-can-raise-said={title === null ? "server" : "title"}>
      <span className="can-raise-mark" aria-hidden="true">
        <Icon name="info" size={16} />
      </span>
      <p className="can-raise-words">
        {title !== null ? (
          <strong className="can-raise-title" data-can-raise-title>{title}</strong>
        ) : (
          <span className="can-raise-server" data-copy-code={canRaise.card.code}>{canRaise.card.text}</span>
        )}{" "}
        <span className="can-raise-later" data-app-copy="canRaise.confirmLater">{prePayText("canRaise.confirmLater")}</span>
      </p>
    </div>
  );
}
